import { type HTMLAttributes, type ReactNode, useRef, useState } from "react";
import { useMove } from "react-aria";
import { createPortal } from "react-dom";

export interface UsePanelResizeProps {
  /** Current size of the panel along the resized axis, in pixels. */
  size: number;
  minSize: number;
  maxSize: number;
  onSizeChange: (size: number) => void;
  /**
   * Edge of the panel the handle sits on. Dragging away from the panel grows
   * it, so a `"left"` handle widens as the pointer moves left and a `"bottom"`
   * handle heightens as the pointer moves down.
   */
  edge: "left" | "right" | "bottom";
  /** Accessible name of the separator, e.g. `"Resize chat panel"`. */
  label: string;
  /** Pixels the size changes per arrow key press. */
  keyboardStep?: number;
  /**
   * Rendered size read once as a move starts, for a panel that sizes itself
   * until it is first resized and so has no stored size to start from.
   */
  measureSize?: () => number | undefined;
}

export interface UsePanelResizeResult {
  /** Spread on the drag handle element. */
  separatorProps: HTMLAttributes<HTMLElement>;
  isResizing: boolean;
  /**
   * Window-wide layer that keeps the pointer over the drag interaction for the
   * whole drag. Render it anywhere; it portals to the document body.
   */
  resizeOverlay: ReactNode;
}

function clampSize(size: number, minSize: number, maxSize: number) {
  return Math.min(maxSize, Math.max(minSize, size));
}

/**
 * Turns an element into a keyboard- and pointer-operable resize separator for
 * a panel, along the axis its `edge` implies.
 *
 * Pointer tracking comes from react-aria's `useMove`, which registers its move
 * listeners on the window synchronously on pointer down — so a fast drag can't
 * outrun the handle — and normalizes touch and arrow-key interaction. The
 * accompanying overlay covers the window while dragging, so hit-testing can't
 * hand the pointer to an iframe or embedded plugin mid-drag and the resize
 * cursor stays put over any element the pointer crosses.
 */
export function usePanelResize({
  size,
  minSize,
  maxSize,
  onSizeChange,
  edge,
  label,
  keyboardStep = 16,
  measureSize,
}: UsePanelResizeProps): UsePanelResizeResult {
  const [isResizing, setIsResizing] = useState(false);
  // Where the drag began and how far it has travelled since, unclamped. Refs
  // because several moves can be processed before React re-renders with the
  // new size, and unclamped so that dragging past a limit and back returns
  // the handle to the pointer instead of leaving it trailing by the overshoot.
  const dragRef = useRef({ startSize: size, offset: 0 });

  const isHeight = edge === "bottom";
  const growDirection = edge === "left" ? -1 : 1;

  const { moveProps } = useMove({
    onMoveStart: () => {
      dragRef.current = { startSize: measureSize?.() || size, offset: 0 };
      setIsResizing(true);
    },
    onMove: ({ deltaX, deltaY, pointerType }) => {
      const delta = isHeight ? deltaY : deltaX;
      // Movement along the separator, such as the other axis's arrow keys, is
      // not a resize.
      if (delta === 0) {
        return;
      }
      const drag = dragRef.current;
      const step = pointerType === "keyboard" ? keyboardStep : 1;
      drag.offset += growDirection * delta * step;
      onSizeChange(clampSize(drag.startSize + drag.offset, minSize, maxSize));
    },
    onMoveEnd: () => setIsResizing(false),
  });

  return {
    isResizing,
    separatorProps: {
      ...moveProps,
      role: "separator",
      "aria-orientation": isHeight ? "horizontal" : "vertical",
      "aria-label": label,
      "aria-valuenow": Math.round(size),
      "aria-valuemin": minSize,
      "aria-valuemax": maxSize,
      tabIndex: 0,
    },
    resizeOverlay: isResizing
      ? createPortal(
          <div
            className={
              isHeight
                ? "fixed inset-0 z-50 cursor-row-resize"
                : "fixed inset-0 z-50 cursor-col-resize"
            }
          />,
          document.body,
        )
      : null,
  };
}
